import React, { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { PresenterProps } from 'src/components/PickingListComponent';
import { PickingList, ShipperList, UnloadList, ItemList } from 'src/entities/pickingListEntity';
import { TruckEntity } from 'src/entities/truckEntity';

import style from './print.module.css';

function PickingHeader({ unload, course }: { unload: UnloadList; course: PickingList }) {
  return (
    <>
      <h1 className={style.picking_title}>ピッキングリスト</h1>

      <div className={style.flex_area}>
        <div className={style.left_pos}>
          <table className={style.driver_table}>
            <tbody>
              <tr>
                <th>運送会社名</th>
                <td style={{ whiteSpace: 'nowrap' }}>{unload.items?.[0]?.item_carrier_company ?? ''}</td>
              </tr>
              <tr>
                <th>車番</th>
                <td style={{ whiteSpace: 'nowrap' }}>{course.truck.license_plate_value}</td>
              </tr>
              <tr>
                <th>最大積載量</th>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {`${course.truck.maximum_loading_capacity_kg}kg`}
                  {course.truck.loading_platform_volume_m3 ? `, ${course.truck.loading_platform_volume_m3}m3` : ''}
                </td>
              </tr>
              <tr>
                <th>ドライバー名</th>
                <td style={{ whiteSpace: 'nowrap' }}>{course.driver.name}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={style.right_pos}>
          <table className={style.stamp_table}>
            <tbody>
              <tr>
                <th>仕分け作業者</th>
                <td colSpan={3} aria-label="empty" />
              </tr>
              <tr>
                <th>検品者</th>
                <td aria-label="empty" />
                <td aria-label="empty" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function DriverInfo({ unload, shipper }: { unload: UnloadList; shipper: ShipperList }) {
  return (
    <table className={style.delivery_table}>
      <tbody>
        <tr>
          <td className={style.label}>積込日</td>
          <td className={style.value}>{unload.items?.[0]?.loading_time_range_start ?? ''}</td>
          <td className={style.label}>納品日</td>
          <td className={style.value}>{unload.items?.[0]?.unloading_time_range_start ?? ''}</td>
        </tr>
      </tbody>
      <tbody>
        <tr>
          <td className={style.label}>荷主名</td>
          <td colSpan={3} className={style.value}>{shipper.shipper_name}</td>
        </tr>
        <tr>
          <td className={style.label}>納品先名</td>
          <td colSpan={3} className={style.value}>{unload.unloading_name}</td>
        </tr>
        <tr>
          <td className={style.label}>納品先住所</td>
          <td colSpan={3} className={style.value}>{unload.items?.[0]?.unloading_address}</td>
        </tr>
      </tbody>
    </table>
  );
}

function GoodsTable({ displayItems }: { displayItems: ItemList[] }) {
  return (
    <table className={style.goods_table}>
      <thead>
        <tr className={style.header}>
          <th style={{ textWrap: 'nowrap' }}>商品CD</th>
          <th style={{ textWrap: 'nowrap' }}>商品名</th>
          <th style={{ textWrap: 'nowrap' }}>ロット</th>
          <th style={{ textWrap: 'nowrap' }}>数量</th>
          <th style={{ textWrap: 'nowrap' }}>荷姿</th>
          <th style={{ textWrap: 'nowrap' }}>備考</th>
        </tr>
      </thead>
      <tbody>
        {displayItems.map((item) => (
          <tr className={style.goods} key={item.item_code || item.empty_id}>
            <td className={style.text_center}>{item.item_code}</td>
            <td className={style.text_left}>{item.item_name}</td>
            <td className={style.item_lot} aria-label="empty" />
            <td className={style.text_center}>{item.item_count !== 0 ? item.item_count : ''}</td>
            <td className={style.text_center}>{item.item_packing_style}</td>
            <td className={style.item_memo} aria-label="empty" />
          </tr>
        ))}
        <tr>
          <td className={style.summery} colSpan={3}>合計</td>
          <td className={style.summery}>
            {displayItems.reduce((prev, current) => prev + current.item_count, 0) !== 0
              ? displayItems.reduce((prev, current) => prev + current.item_count, 0) : ''}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function PickingFooter({ truck, currentPage, totalPage }: { truck: TruckEntity; currentPage: number; totalPage: number }) {
  return (
    <div className={style.footer}>
      <div className={style.dispatch}>{truck.sort_order && `コース：${truck.sort_order}`}</div>
      <div className={style.issue}>
        {`${formatDateTime(new Date())} 発行 `}
        {`<${currentPage}/${totalPage}>`}
      </div>
    </div>
  );
}

const emptyData: ItemList[] = Array.from(
  { length: 10 },
  (_, i) => ({
    item_carrier_company: '',
    item_code: '',
    item_count: 0,
    item_name: '',
    item_packing_style: '',
    loading_time_range_start: '',
    unloading_address: '',
    unloading_time_range_start: '',
    empty_id: i
  })
);

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

const PickingListPresenter: FC<PresenterProps> = memo(({ models, date, unit }) => {
  let pageCountCurrent = 0;

  const pageCountTotal = models.reduce((total, course) => {
    // データが空の場合も1ページ分追加
    if (course.dispatches.length === 0) {
      return total + 1;
    }

    // データがある場合は、荷主・納品先毎にページを追加
    const courseCount = course.dispatches.reduce((dispatchTotal, loadingDate) => {
      const dateCount = loadingDate.shippers.reduce((shipperTotal, shipper) => shipperTotal + shipper.unloads.length, 0);
      return dispatchTotal + dateCount;
    }, 0);
    return total + courseCount;
  }, 0);

  return (
    <>
      <Helmet>
        <title>ピッキングリスト</title>
      </Helmet>
      <div className={style.main}>
        {models.map((course) => (
          <React.Fragment key={`${course.truck.license_plate_value}-${course.driver.name}`}>
            {course.dispatches.length === 0 ? (
              <div className={style.page} style={{ page: 'A4PickingList' }}>
                <PickingHeader unload={{ unloading_name: '', items: [] }} course={course} />
                <DriverInfo unload={{ unloading_name: '', items: [] }} shipper={{ shipper_name: '', unloads: [] }} />
                <GoodsTable displayItems={emptyData} />
                <PickingFooter truck={course.truck} currentPage={++pageCountCurrent} totalPage={pageCountTotal} />
              </div>
            ) : (
              course.dispatches.map((loadDate) => (
                <React.Fragment key={`${course.truck.sort_order}-${loadDate.date}`}>
                  {loadDate.shippers.map((shipper) => (
                    <React.Fragment key={`${course.truck.sort_order}-${loadDate.date}-${shipper.shipper_name}`}>
                      {shipper.unloads.map((unload) => {
                        const displayRowCount = unload.items.length === 0 ? 10 : Math.max(unload.items.length, 20);

                        const emptyRows: ItemList[] = Array.from(
                          { length: displayRowCount - unload.items.length },
                          (_, i) => ({
                            item_carrier_company: '',
                            item_code: '',
                            item_count: 0,
                            item_name: '',
                            item_packing_style: '',
                            loading_time_range_start: '',
                            unloading_address: '',
                            unloading_time_range_start: '',
                            empty_id: i
                          })
                        );
                        const displayItems = [...unload.items, ...emptyRows];
                        return (
                          <div className={style.page} style={{ page: 'A4PickingList' }} key={`${course.truck.sort_order}-${loadDate.date}-${shipper.shipper_name}-${unload.unloading_name}`}>
                            <PickingHeader unload={unload} course={course} />
                            <DriverInfo unload={unload} shipper={shipper} />
                            <GoodsTable displayItems={displayItems} />
                            <PickingFooter truck={course.truck} currentPage={++pageCountCurrent} totalPage={pageCountTotal} />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
});

export default PickingListPresenter;
